/**
 * AdminEDepots — Admin management of e-Deposits (v50.10.1).
 * Super admin only. Lists all deposits, allows downloading certificates and
 * original files, and creating a FREE test deposit (bypasses Stripe payment).
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Upload, Loader2, Plus, ShieldCheck, RefreshCw } from 'lucide-react';
import { useAdminLang } from '../hooks/useAdminLang';

const AdminEDepots = () => {
  const navigate = useNavigate();
  const { at } = useAdminLang();
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploaded, setUploaded] = useState(null);
  const [form, setForm] = useState({ title: '', work_type: 'text', description: '', duration_years: 5 });
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/edepots/admin/all');
      setDeposits(r.data?.deposits || []);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await axios.post('/api/edepots/upload', fd);
      setUploaded({
        file_id: r.data.file_id, file_name: f.name,
        file_size: f.size, file_hash_sha256: r.data.hash,
        file_mime: f.type || 'application/octet-stream',
      });
      toast.success('Fichier uploadé — remplis le titre puis crée le dépôt');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur upload');
    } finally { setUploadBusy(false); }
  };

  const createFree = async () => {
    if (!uploaded) { toast.error('Upload d\'abord un fichier'); return; }
    if (!form.title.trim()) { toast.error('Titre requis'); return; }
    setCreating(true);
    try {
      const r = await axios.post('/api/edepots/admin/create-free', {
        title: form.title, work_type: form.work_type,
        description: form.description || null, duration_years: form.duration_years,
        ...uploaded, co_authors: [],
      });
      toast.success(`Dépôt admin créé ✅ (id ${r.data.deposit_id.slice(0, 8)})`);
      setUploaded(null);
      setForm({ title: '', work_type: 'text', description: '', duration_years: 5 });
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création');
    } finally { setCreating(false); }
  };

  const download = async (deposit_id, kind) => {
    try {
      const r = await axios.get(`/api/edepots/admin/${deposit_id}/${kind}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = kind === 'certificate' ? `attestation_${deposit_id.slice(0, 8)}.pdf` : `depot_${deposit_id.slice(0, 8)}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur téléchargement');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-950 to-purple-950/40 text-white pb-24">
      <header className="sticky top-0 z-30 bg-black/70 backdrop-blur-md border-b border-white/10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/admin')} className="p-2 -ml-2 rounded-full hover:bg-white/10" data-testid="edepots-back"><ArrowLeft className="w-5 h-5" /></button>
          <ShieldCheck className="w-5 h-5 text-fuchsia-400" />
          <h1 className="font-bold text-base sm:text-lg flex-1">{at('edepotsAdminTitle') || 'e-Dépôts — Gestion & test'}</h1>
          <button onClick={load} className="p-2 rounded-full hover:bg-white/10"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 pt-4 space-y-4">
        {/* Create a free admin deposit */}
        <div className="p-4 rounded-2xl bg-fuchsia-500/10 border border-fuchsia-500/30 space-y-3" data-testid="edepots-admin-create-panel">
          <h2 className="font-semibold text-sm text-fuchsia-200 flex items-center gap-1.5"><Plus className="w-4 h-4" /> {at('edepotsCreateFree') || 'Créer un dépôt admin (gratuit — sans Stripe)'}</h2>
          <input ref={fileRef} type="file" onChange={onFileChange} className="w-full text-xs text-white/80" data-testid="edepots-admin-file" />
          {uploadBusy && <div className="text-xs text-white/60 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Upload…</div>}
          {uploaded && <div className="text-[11px] text-emerald-300">✓ {uploaded.file_name} · {(uploaded.file_size / 1024).toFixed(1)} Ko · hash {uploaded.file_hash_sha256.slice(0, 12)}…</div>}

          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
            placeholder={at('edepotsTitle') || 'Titre de l\u2019œuvre'}
            className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-white" data-testid="edepots-admin-title" />

          <div className="grid grid-cols-2 gap-2">
            <select value={form.work_type} onChange={e => setForm({ ...form, work_type: e.target.value })}
              className="bg-black/30 border border-white/10 rounded-xl px-2 py-2 text-xs text-white" data-testid="edepots-admin-worktype">
              <option value="text">Texte / Manuscrit</option>
              <option value="music">Musique / Son</option>
              <option value="image">Photo / Illustration</option>
              <option value="video">Vidéo</option>
              <option value="software">Logiciel / Code</option>
              <option value="other">Autre</option>
            </select>
            <select value={form.duration_years} onChange={e => setForm({ ...form, duration_years: parseInt(e.target.value, 10) })}
              className="bg-black/30 border border-white/10 rounded-xl px-2 py-2 text-xs text-white" data-testid="edepots-admin-duration">
              {[1, 5, 10, 25].map(y => <option key={y} value={y}>{y} an{y > 1 ? 's' : ''}</option>)}
            </select>
          </div>
          <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
            placeholder={at('edepotsDescription') || 'Description (optionnel)'} rows={2}
            className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-xs text-white resize-none" data-testid="edepots-admin-desc" />

          <button onClick={createFree} disabled={!uploaded || !form.title.trim() || creating}
            className="w-full py-2 rounded-full bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white text-sm font-semibold disabled:opacity-40 inline-flex items-center justify-center gap-2"
            data-testid="edepots-admin-create-btn">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {at('edepotsCreateBtn') || 'Créer le dépôt admin gratuit'}
          </button>
        </div>

        {/* All deposits */}
        <h2 className="font-semibold text-sm text-white/80 uppercase tracking-wider">{at('edepotsAll') || 'Tous les dépôts'}</h2>
        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-fuchsia-400" /></div>
        ) : deposits.length === 0 ? (
          <p className="text-sm text-gray-400">Aucun dépôt.</p>
        ) : deposits.map(d => (
          <div key={d.id} className="p-3 bg-white/5 border border-white/10 rounded-2xl" data-testid={`edepot-row-${d.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">{d.title}</div>
                <div className="text-[10px] text-white/50">
                  {d.work_type} · {d.status} · déposé {(d.deposited_at || d.created_at || '').slice(0, 10)}
                  {d.admin_free && ' · 🆓 admin'}
                </div>
                {d.user_email && <div className="text-[10px] text-white/40">👤 {d.user_email}</div>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => download(d.id, 'certificate')}
                className="px-3 py-1.5 rounded-full bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200 text-xs inline-flex items-center gap-1.5"
                data-testid={`edepot-cert-${d.id}`}>
                <FileText className="w-3.5 h-3.5" /> {at('edepotsCert') || 'Attestation PDF'}
              </button>
              <button onClick={() => download(d.id, 'file')}
                className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs inline-flex items-center gap-1.5"
                data-testid={`edepot-file-${d.id}`}>
                <Download className="w-3.5 h-3.5" /> {at('edepotsFile') || 'Fichier original'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminEDepots;
